/**
 * 🎮 GESTOR DE ESTADOS DE LA APLICACIÓN AR
 * Maneja las transiciones entre estados: initial → red/blue → initial
 */

import { APP_STATES } from '@config/constants';
import { safeQuerySelector } from '@utils/domUtils';
import type {
  AppState,
  StateChangeCallback,
  StateInfo,
  DOMElements
} from '@types';
import { pauseVideo, playVideoSafely } from '@/utils/videoUtils';

export class StateManager {
  private currentState: AppState = APP_STATES.INITIAL;
  private elements: DOMElements = {};
  private stateChangeCallbacks: StateChangeCallback[] = [];
  private currentSolutionContent: string | null = null;
  private selectedSolutionButton: string | null = null;

  constructor() {
    this.initializeElements();
  }

  /**
   * Inicializa referencias a elementos DOM
   */
  private initializeElements(): void {
    const selectors = {
      homeState: '#homeState',
      solutionsState: '#solutionsState',
      solutionsContent: '#solutionsContent',
      authenticationContent: '#authenticationContent',
      identityContent: '#identityContent',
      onboardingContent: '#onboardingContent',
      authenticationText: '#authenticationText',
      initialState: '#initialState',
      videoInitial: '#videoInitial',
      facephiButton: '#facephiButton'
    };

    for (const [name, selector] of Object.entries(selectors)) {
      this.elements[name] = safeQuerySelector(selector);

      if (!this.elements[name]) {
        console.warn(`StateManager: Elemento no encontrado - ${name}`);
      }
    }
  }

  /**
   *  Re-inicializa elementos (útil después de que A-Frame cargue)
   */
  public reinitializeElements(): void {
    console.log('Re-inicializando elementos del StateManager...');
    this.initializeElements();
  }

  /**
   *  Obtiene el estado actual
   */
  getCurrentState(): AppState {
    return this.currentState;
  }

  /**
   *  Cambia al estado especificado
   */
  changeState(newState: AppState, options: Record<string, any> = {}): boolean {
    if (!this.isValidTransition(this.currentState, newState)) {
      console.warn(`Transición inválida: ${this.currentState} → ${newState}`);
      return false;
    }

    const previousState = this.currentState;
    this.currentState = newState;

    // Ejecutar transición
    this.executeStateTransition(previousState, newState, options);

    console.log(`Estado cambiado: ${previousState} → ${newState}`);

    // Notificar callbacks con un pequeño delay para que los elementos se rendericen
    setTimeout(() => {
      this.notifyStateChange(previousState, newState);
    }, 150);

    return true;
  }

  /**
   * Valida si una transición es permitida
   */
  private isValidTransition(from: AppState, to: AppState): boolean {
    const validTransitions: Partial<Record<AppState, AppState[]>> = {
      [APP_STATES.INITIAL]: [APP_STATES.HOME],
      [APP_STATES.HOME]: [APP_STATES.SOLUTIONS, APP_STATES.INITIAL],
      [APP_STATES.SOLUTIONS]: [APP_STATES.HOME]
    };

    return validTransitions[from]?.includes(to) || false;
  }

  /**
   * Ejecuta la transición entre estados
   */
  private executeStateTransition(
    from: AppState,
    to: AppState,
    options: Record<string, any>
  ): void {
    // Ocultar contenido del estado anterior
    this.hideStateContent(from);

    // Mostrar contenido del nuevo estado
    this.showStateContent(to, options);
  }

  /**
   * Oculta el contenido de un estado
   */
  private hideStateContent(state: AppState): void {
    switch (state) {
      case APP_STATES.HOME:
        this.setElementVisibility('homeState', false);
        break;

      case APP_STATES.SOLUTIONS:
        this.setElementVisibility('solutionsState', false);
        this.setElementVisibility('solutionsContent', false);
        this.hideAllSolutionContents();
        pauseVideo();
        this.resetButtonSelection();
        this.currentSolutionContent = null;
        break;

      case APP_STATES.INITIAL:
        this.setElementVisibility('initialState', false);
        break;
    }
  }

  /**
   *  Muestra el contenido de un estado
   */
  private showStateContent(state: AppState, options: Record<string, any>): void {
    switch (state) {
      case APP_STATES.HOME:
        this.setElementVisibility('homeState', true);
        break;

      case APP_STATES.SOLUTIONS:
        // Mostrar los botones de productos
        this.setElementVisibility('solutionsState', true);
        // Si se especifica una solución específica, mostrar su contenido
        if (options.solutionId) {
          this.showSolutionContent(options.solutionId);
        } else {
          // Por defecto, mostrar el contenido de AU automáticamente
          this.showSolutionContent('auButton');
        }
        break;

      case APP_STATES.INITIAL:
        this.setElementVisibility("initialState", true);
        break;
    }
  }

  /**
   * Establece la visibilidad de un elemento A-Frame
   */
  private setElementVisibility(elementName: string, visible: boolean): void {
    const element = this.elements[elementName];
    if (element) {
      element.setAttribute('visible', visible.toString());
    } else {
      console.warn(`Elemento no encontrado para visibilidad: ${elementName}`);
    }
  }

  /**
   * Muestra el contenido de una solución específica
   */
  private showSolutionContent(solutionId: string): void {
    this.currentSolutionContent = solutionId;

    // Actualizar selección visual ANTES de ocultar la entidad
    this.updateButtonSelection(solutionId);

    // Configuración de contenido para cada solución
    const solutionData = this.getSolutionData(solutionId);

    if (solutionData) {
      // Mostrar el contenedor principal
      this.setElementVisibility('solutionsContent', true);

      // Ocultar todos los contenidos específicos
      this.hideAllSolutionContents();

      // Mostrar solo el contenido específico
      this.showSpecificSolutionContent(solutionId);

      // Video no se reproduce automáticamente
      // if (solutionData.hasVideo) {
      //   this.playVideo();
      // }

      console.log(`✅ Mostrando contenido de: ${solutionData.title}`);
    } else {
      console.error(`❌ No se encontraron datos para: ${solutionId}`);
    }
  }

  /**
   * Oculta todos los contenidos específicos de soluciones
   */
  private hideAllSolutionContents(): void {
    this.setElementVisibility('authenticationContent', false);
    this.setElementVisibility('identityContent', false);
    this.setElementVisibility('onboardingContent', false);
    
    // Limpiar event listeners
    this.cleanupVideoEventListeners();
  }

  /**
   * Limpia los event listeners de los videos de soluciones
   */
  private cleanupVideoEventListeners(): void {
    const videoSelectors = ['#videoAuthentication', '#videoIDV', '#videoOnboarding'];
    
    videoSelectors.forEach(selector => {
      const videoElement = document.querySelector(selector) as HTMLVideoElement;
      if (videoElement) {
        const existingTimeUpdateHandler = (videoElement as any)._timeUpdateHandler;
        if (existingTimeUpdateHandler) {
          videoElement.removeEventListener('timeupdate', existingTimeUpdateHandler);
          (videoElement as any)._timeUpdateHandler = null;
        }
      }
    });
  }

  /**
   * Muestra el contenido específico de una solución
   */
  private showSpecificSolutionContent(solutionId: string): void {
    const contentMap: Record<string, string> = {
      'auButton': 'authenticationContent',
      'isButton': 'identityContent',
      'onButton': 'onboardingContent'
    };

    const contentElementName = contentMap[solutionId];
    if (contentElementName) {
      this.setElementVisibility(contentElementName, true);
      console.log(`Mostrando contenido específico: ${contentElementName}`);

      // Verificar estado del video
      this.checkVideoStatus(solutionId);
    } else {
      console.warn(`No se encontró mapeo de contenido para: ${solutionId}`);
    }
  }

  /**
   * Verifica el estado del video para debugging
   */
  private checkVideoStatus(solutionId: string): void {
    const videoMap: Record<string, string> = {
      'auButton': '#videoAuthentication',
      'isButton': '#videoIDV',
      'onButton': '#videoOnboarding'
    };

    const videoSelector = videoMap[solutionId];
    if (videoSelector) {
      const videoElement = document.querySelector(videoSelector) as HTMLVideoElement;
      if (videoElement) {
        console.log(`📹 Video ${videoSelector}:`, {
          src: videoElement.src,
          readyState: videoElement.readyState,
          videoWidth: videoElement.videoWidth,
          videoHeight: videoElement.videoHeight,
          duration: videoElement.duration,
          paused: videoElement.paused,
          muted: videoElement.muted
        });

        // Remover event listeners anteriores para evitar duplicados
        const existingTimeUpdateHandler = (videoElement as any)._timeUpdateHandler;
        if (existingTimeUpdateHandler) {
          videoElement.removeEventListener('timeupdate', existingTimeUpdateHandler);
        }

        const timeUpdateHandler = () => {
          // Solo actuar si estamos en el estado SOLUTIONS
          if (this.currentState !== APP_STATES.SOLUTIONS) {
            return;
          }
          
          const timeRemaining = 0.5; // medio segundo antes de que termine el vídeo
          // Si el tiempo restante para que termine el vídeo es menor a 0.5 segundos, reiniciar el video
          if (videoElement.duration - videoElement.currentTime <= timeRemaining) {
            videoElement.currentTime = 0;
          }
        };

        // Guardar referencia al handler para poder removerlo después
        (videoElement as any)._timeUpdateHandler = timeUpdateHandler;

        // Esperar a que el video esté listo antes de reproducir
        if (videoElement.readyState >= 2) { 
          playVideoSafely(videoElement, videoSelector);
          videoElement.addEventListener('timeupdate', timeUpdateHandler);
        } else {
          videoElement.addEventListener('loadeddata', () => {
            playVideoSafely(videoElement, videoSelector);
            videoElement.addEventListener('timeupdate', timeUpdateHandler);
          }, { once: true });
        }
      } else {
        console.error(`No se encontró elemento de video: ${videoSelector}`);
      }
    }
  }

  /**
   * Actualiza la selección visual de botones
   */
  private updateButtonSelection(selectedButtonId: string): void {
    const solutionButtons = ['auButton', 'isButton', 'onButton'];

    solutionButtons.forEach(buttonId => {
      const buttonElement = safeQuerySelector(`#${buttonId}`);
      if (buttonElement) {
        if (buttonId === selectedButtonId) {
          // Botón seleccionado - usar imagen _select
          const selectedSrc = buttonElement.getAttribute('data-selected');
          if (selectedSrc) {
            buttonElement.setAttribute('src', selectedSrc);
            this.selectedSolutionButton = buttonId;
          }
        } else {
          // Botón no seleccionado - usar imagen normal
          const normalSrc = buttonElement.getAttribute('data-normal');
          if (normalSrc) {
            buttonElement.setAttribute('src', normalSrc);
          }
        }
      }
    });
  }

  /**
   * Resetea la selección visual de botones
   */
  private resetButtonSelection(): void {
    const solutionButtons = ['auButton', 'isButton', 'onButton'];

    solutionButtons.forEach(buttonId => {
      const buttonElement = safeQuerySelector(`#${buttonId}`);
      if (buttonElement) {
        const normalSrc = buttonElement.getAttribute('data-normal');
        if (normalSrc) {
          buttonElement.setAttribute('src', normalSrc);
        }
      }
    });

    this.selectedSolutionButton = null;
  }

  /**
   * Obtiene los datos de una solución
   */
  private getSolutionData(solutionId: string): any {
    const solutionsData: Record<string, any> = {
      'auButton': {
        title: 'Authentication',
        description: 'Soluciones de autenticación biométrica avanzada para verificar la identidad de usuarios de forma segura y eficiente.',
        hasVideo: true,
        videoSrc: '/assets/videos/authentication.webm'
      },
      'isButton': {
        title: 'Identity',
        description: 'Tecnologías de verificación de identidad que garantizan la autenticidad de documentos y personas en tiempo real.',
        hasVideo: true,
        videoSrc: '/assets/videos/identity.webm'
      },
      'onButton': {
        title: 'Onboarding',
        description: 'Procesos de incorporación digital seguros y fluidos que mejoran la experiencia del usuario desde el primer contacto.',
        hasVideo: true,
        videoSrc: '/assets/videos/onboarding.webm'
      }
    };

    return solutionsData[solutionId] || null;
  }



  /**
   * Registra callback para cambios de estado
   */
  onStateChange(callback: StateChangeCallback): void {
    if (typeof callback === 'function') {
      this.stateChangeCallbacks.push(callback);
    }
  }

  /**
   *  Notifica a todos los callbacks sobre cambio de estado
   */
  private notifyStateChange(from: AppState, to: AppState): void {
    this.stateChangeCallbacks.forEach(callback => {
      try {
        callback(from, to);
      } catch (error) {
        console.error('Error en callback de cambio de estado:', error);
      }
    });
  }

  /**
   *  Maneja clicks en botones según el estado actual
   */
  handleButtonClick(buttonId: string): boolean {
    // Primero verificar si el botón tiene una URL para navegar
    const buttonElement = this.getButtonElement(buttonId);
    if (buttonElement) {
      const url = buttonElement.getAttribute('data-link');
      if (url) {
        console.log(`Navegando a URL desde botón ${buttonId}: ${url}`);
        window.open(url, '_blank', 'noopener,noreferrer');
        return true;
      }
    }

    // Si no tiene URL, manejar como cambio de estado
    const actions: Partial<Record<AppState, Record<string, () => void>>> = {
      [APP_STATES.HOME]: {
        'facephiButton': () => {
          // Al hacer clic en el botón de Facephi, oculta el Home y vuelve a reproducir el vídeo principal
          console.log('Botón de Facephi clickeado');
          this.changeState(APP_STATES.INITIAL);
          const initialState = document.querySelector('#initialState');
          const video = this.elements.videoInitial as HTMLVideoElement;
          if (initialState && video) {
            // Reinicia el vídeo para que empiece desde el principio
            video.currentTime = 0;
            playVideoSafely(video, '#videoInitial');
          }
        },
        'webButton': () => {
          // El botón web ya tiene su URL en data-link
          console.log('Botón web clickeado');
        },
        'linkedinButton': () => {
          // El botón linkedin ya tiene su URL en data-link
          console.log('Botón linkedin clickeado');
        },
        'locationButton': () => {
          // El botón de ubicación ya tiene su URL en data-link
          console.log('Botón de ubicación clickeado');
        },
        'solutionButton': () => this.changeState(APP_STATES.SOLUTIONS),
      },
      [APP_STATES.SOLUTIONS]: {
        'auButton': () => this.showSolutionContent('auButton'),
        'isButton': () => this.showSolutionContent('isButton'),
        'onButton': () => this.showSolutionContent('onButton'),
        'goHomeButton': () => this.changeState(APP_STATES.HOME),
      }
    };

    const stateActions = actions[this.currentState];
    const action = stateActions?.[buttonId];

    if (action) {
      action();
      return true;
    }

    console.log(`No hay acción para botón ${buttonId} en estado ${this.currentState}`);
    return false;
  }

  /**
   * Obtiene el elemento DOM de un botón por su ID
   */
  private getButtonElement(buttonId: string): Element | null {
    return document.querySelector(`#${buttonId}`);
  }

  /**
   *  Resetea al estado inicial
   */
  reset(): void {
    this.changeState(APP_STATES.HOME);
  }

  /**
   * Obtiene información del estado actual
   */
  getStateInfo(): StateInfo {
    return {
      current: this.currentState,
      availableTransitions: this.getAvailableTransitions(),
      elementsVisible: this.getVisibleElements()
    };
  }

  /**
   * Obtiene transiciones disponibles desde el estado actual
   */
  private getAvailableTransitions(): AppState[] {
    const transitions: Partial<Record<AppState, AppState[]>> = {
      [APP_STATES.HOME]: [APP_STATES.SOLUTIONS],
      [APP_STATES.SOLUTIONS]: [APP_STATES.HOME]
    };

    return transitions[this.currentState] || [];
  }

  /**
   * Obtiene elementos visibles en el estado actual
   */
  private getVisibleElements(): string[] {
    const visibilityMap: Partial<Record<AppState, string[]>> = {
      [APP_STATES.INITIAL]: ['initialState'],
      [APP_STATES.HOME]: ['homeState'],
      [APP_STATES.SOLUTIONS]: this.currentSolutionContent
        ? ['solutionsState', 'solutionsContent']
        : ['solutionsState']
    };

    return visibilityMap[this.currentState] || [];
  }

  /**
   * Obtiene información del botón seleccionado
   */
  getSelectedButton(): string | null {
    return this.selectedSolutionButton;
  }

  /**
   * Limpia recursos del gestor de estados
   */
  cleanup(): void {
    this.stateChangeCallbacks = [];
    console.log('StateManager limpiado');
  }
}